/**
 * Orders page top filter bar.
 *
 * Renders, top-to-bottom:
 *   1. Company shortcuts (Toutes / Fuji Express / Concept Lab / My Food / Boucherie)
 *   2. Sites FE shortcuts (Génipa / Cluny / ... / Siège)
 *   3. Supplier shortcuts (editable, persisted by parent)
 *   4. Period shortcuts (Tout / Semaine / 2 sem / Mois / 3 mois / 1 an / Plage)
 *   5. Filter row (search input + supplier/site/status/type selects)
 *
 * Extracted from OrdersTab.jsx (≈210 lines) — pure presentational, all state lives in the parent.
 */
import React from 'react';
import { Search, X } from 'lucide-react';
import { supplierShortcutLabel } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

// Appui long (~500 ms, souris ou tactile) et clic droit = mode exclusion.
function longPressHandlers(onLong, onShort) {
    let timer = null;
    let longFired = false;
    return {
        onPointerDown: (e) => {
            if (e.button === 2) return;
            longFired = false;
            timer = setTimeout(() => { timer = null; longFired = true; onLong(); }, 500);
        },
        onPointerUp: () => { if (timer) { clearTimeout(timer); timer = null; } },
        onPointerLeave: () => { if (timer) { clearTimeout(timer); timer = null; } },
        onClick: () => {
            if (longFired) { longFired = false; return; }
            onShort();
        },
        onContextMenu: (e) => {
            e.preventDefault();
            if (timer) { clearTimeout(timer); timer = null; }
            if (!longFired) onLong();
            longFired = false;
        },
    };
}

export default function OrdersFilterBar({
    // Filter state
    filterCompany, setFilterCompany,
    filterSite, setFilterSite,
    filterSupplier, setFilterSupplier,
    filterStatus, setFilterStatus,
    filterType, setFilterType,
    filterPeriod, setFilterPeriod,
    customPeriodStart, setCustomPeriodStart,
    customPeriodEnd, setCustomPeriodEnd,
    searchTerm, setSearchTerm,
    // Data
    sites,
    suppliers,
    // Supplier shortcut config
    supplierShortcutNames,
    onEditSupplierShortcuts,
    resolveShortcutSupplier,
    normalizeStr,
    // Period presets dictionary
    periodPresets,
    // Company matcher dictionary keys (Object.keys used by parent)
    companyKeys,
    // Visible/allowed sites for the user
    allowedSites,
    // Exclusions temporaires (appui long)
    excludedSites = [],
    excludedCompanies = [],
    onToggleExcludeSite = () => {},
    onToggleExcludeCompany = () => {},
    onClearExclusions = () => {},
}) {
    const hasExclusions = excludedSites.length > 0 || excludedCompanies.length > 0;
    return (
        <>
            {/* Company + sites + suppliers + period shortcuts */}
            <div className="flex flex-col lg:flex-row lg:items-start gap-3 lg:gap-8">
                <div className="flex flex-col gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="company-shortcuts">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1">Entreprise :</span>
                        {['all', ...companyKeys].map((company) => {
                            const isActive = filterCompany === company;
                            const isExcluded = company !== 'all' && excludedCompanies.includes(company);
                            const label = company === 'all' ? 'Toutes' : company;
                            const handlers = company === 'all'
                                ? { onClick: () => { if (filterSite !== 'all') setFilterSite('all'); setFilterCompany('all'); } }
                                : longPressHandlers(
                                    () => onToggleExcludeCompany(company),
                                    () => {
                                        if (isExcluded) { onToggleExcludeCompany(company); return; }
                                        if (filterSite !== 'all') setFilterSite('all');
                                        setFilterCompany(company);
                                    },
                                );
                            return (
                                <button
                                    key={company}
                                    type="button"
                                    {...handlers}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border select-none ${
                                        isExcluded
                                            ? 'bg-red-50 text-red-600 border-red-400 line-through'
                                            : isActive
                                                ? 'bg-slate-900 text-white border-slate-900'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    style={{ WebkitTouchCallout: 'none' }}
                                    title={company === 'all' ? undefined : (isExcluded ? 'Exclu — cliquer pour réintégrer' : 'Appui long : exclure de la sélection')}
                                    data-testid={`company-shortcut-${company}`}
                                >
                                    {isExcluded ? `− ${label}` : label}
                                </button>
                            );
                        })}
                    </div>
                    {/* Ligne 2 : raccourcis sites Fuji Express */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Sites FE :</span>
                        {['Génipa', 'Cluny', 'Dillon', 'Océanis', 'Vauclin', 'Siège'].map((shortName) => {
                            const fullName = `FE ${shortName}`;
                            const site = sites.find(s => s.name === fullName);
                            const isActive = site && filterSite === site.id;
                            const isExcluded = site && excludedSites.includes(site.id);
                            const handlers = site ? longPressHandlers(
                                () => onToggleExcludeSite(site.id),
                                () => {
                                    if (isExcluded) { onToggleExcludeSite(site.id); return; }
                                    // Un site FE implique l'entreprise Fuji Express
                                    setFilterCompany(isActive ? 'all' : 'Fuji Express');
                                    setFilterSite(isActive ? 'all' : site.id);
                                },
                            ) : {};
                            return (
                                <button
                                    key={shortName}
                                    type="button"
                                    disabled={!site}
                                    {...handlers}
                                    className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center select-none ${
                                        !site
                                            ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                            : isExcluded
                                                ? 'bg-red-50 text-red-600 border-red-400 line-through'
                                                : isActive
                                                    ? 'bg-slate-900 text-white border-slate-900'
                                                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                    }`}
                                    style={{ WebkitTouchCallout: 'none' }}
                                    title={!site ? undefined : (isExcluded ? 'Exclu — cliquer pour réintégrer' : 'Appui long : exclure de la sélection')}
                                    data-testid={`site-shortcut-${shortName.toLowerCase()}`}
                                >
                                    {isExcluded ? `− ${shortName}` : shortName}
                                </button>
                            );
                        })}
                    </div>
                    {hasExclusions && (
                        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="exclusions-summary">
                            <span className="font-semibold text-red-600">
                                Sélection : {filterCompany === 'all' ? 'Toutes' : filterCompany}
                                {excludedCompanies.map(c => ` − ${c}`).join('')}
                                {excludedSites.map(id => {
                                    const s = sites.find(x => x.id === id);
                                    return s ? ` − ${s.name.replace(/^FE /, '')}` : '';
                                }).join('')}
                            </span>
                            <button
                                type="button"
                                onClick={onClearExclusions}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50"
                                data-testid="clear-exclusions-btn"
                            >
                                <X className="w-3 h-3" /> Réinitialiser
                            </button>
                        </div>
                    )}
                    {/* Ligne 3 : raccourcis fournisseurs (editables) */}
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Fournisseurs :</span>
                        {supplierShortcutNames.map((name, idx) => {
                            const supplier = resolveShortcutSupplier(name);
                            const isActive = supplier && filterSupplier === supplier.id;
                            return (
                                <button
                                    key={`${name}-${idx}`}
                                    type="button"
                                    disabled={!supplier}
                                    onClick={() => supplier && setFilterSupplier(isActive ? 'all' : supplier.id)}
                                    className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                        !supplier
                                            ? 'bg-slate-100 text-slate-300 border-slate-100 cursor-not-allowed'
                                            : isActive
                                                ? 'bg-orange-600 text-white border-orange-600'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-orange-300'
                                    }`}
                                    title={!supplier ? `Fournisseur introuvable : ${name}` : supplier.name}
                                    data-testid={`supplier-shortcut-${normalizeStr(name)}`}
                                >
                                    {supplierShortcutLabel(supplier?.name || name)}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={onEditSupplierShortcuts}
                            className="px-2.5 py-1.5 rounded-full text-xs font-semibold text-slate-500 border border-dashed border-slate-300 hover:border-orange-400 hover:text-orange-600 flex-shrink-0"
                            title="Modifier la liste des raccourcis"
                            data-testid="edit-supplier-shortcuts-btn"
                        >
                            Modifier
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-orange-50/50 border border-orange-100 rounded-lg" data-testid="period-shortcuts">
                    <span className="text-xs font-semibold text-slate-500 mr-1">Période :</span>
                    {Object.entries(periodPresets).map(([key, preset]) => {
                        const isActive = filterPeriod === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setFilterPeriod(key)}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                    isActive
                                        ? 'bg-orange-500 text-white border-orange-500'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-orange-300'
                                }`}
                                data-testid={`period-shortcut-${key}`}
                            >
                                {preset.label}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        onClick={() => setFilterPeriod('custom')}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                            filterPeriod === 'custom'
                                ? 'bg-orange-500 text-white border-orange-500'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-orange-300'
                        }`}
                        data-testid="period-shortcut-custom"
                    >
                        Plage
                    </button>
                    {filterPeriod === 'custom' && (
                        <div className="flex items-center gap-1 ml-1">
                            <input
                                type="date"
                                value={customPeriodStart}
                                onChange={(e) => setCustomPeriodStart(e.target.value)}
                                className="h-8 px-2 text-xs border border-slate-300 rounded"
                                data-testid="period-custom-start"
                            />
                            <span className="text-slate-400">→</span>
                            <input
                                type="date"
                                value={customPeriodEnd}
                                onChange={(e) => setCustomPeriodEnd(e.target.value)}
                                className="h-8 px-2 text-xs border border-slate-300 rounded"
                                data-testid="period-custom-end"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Filters card */}
            <Card>
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="N° commande..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-orders-input"
                            />
                        </div>
                        <Select value={filterSupplier} onValueChange={setFilterSupplier}>
                            <SelectTrigger data-testid="filter-supplier-select">
                                <SelectValue placeholder="Fournisseur" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les fournisseurs</SelectItem>
                                {[...suppliers].sort((a, b) => a.name.localeCompare(b.name, 'fr')).map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterSite} onValueChange={setFilterSite}>
                            <SelectTrigger data-testid="filter-site-select">
                                <SelectValue placeholder="Site" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les sites</SelectItem>
                                {allowedSites.map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger data-testid="filter-status-select">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les commandes</SelectItem>
                                <SelectItem value="received">Réceptionnées / Livrées</SelectItem>
                                <SelectItem value="in_progress">En cours</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger data-testid="filter-type-select">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les types</SelectItem>
                                <SelectItem value="draft">Saisies</SelectItem>
                                <SelectItem value="ordered">Commandés</SelectItem>
                                <SelectItem value="reception">Réceptions</SelectItem>
                                <SelectItem value="transfer_in">Transferts reçus</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>
        </>
    );
}
